<template>
  <div class="fin-ss" ref="root">
    <button type="button" class="fin-ss-toggle" :title="triggerTitle" @click="toggle">
      <span class="fin-ss-toggle-label">{{ triggerLabel }}</span>
      <i class="fa" :class="open ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
    </button>
    <Teleport to="body">
      <div v-if="open" class="fin-ss-menu" :style="menuStyle" @mousedown.stop>
        <div class="fin-ss-search">
          <i class="fa fa-search"></i>
          <input
            ref="searchInput"
            v-model="query"
            type="text"
            :placeholder="searchPlaceholder"
            @keydown.esc.stop.prevent="close"
          />
        </div>
        <div v-if="multiple" class="fin-ss-actions">
          <button type="button" class="fin-link-btn" @click="selectFiltered">Zgjidh të gjitha</button>
          <button type="button" class="fin-link-btn" :disabled="!hasSelection" @click="clear">Hiq të gjitha</button>
        </div>
        <div class="fin-ss-list">
          <button
            v-if="multiple && selectAllLabel && !grouped"
            type="button"
            class="fin-ss-option"
            :class="{ checked: allOptionsSelected }"
            @click="toggleSelectAll"
          >
            <span class="fin-ss-check" aria-hidden="true">
              <i v-if="allOptionsSelected" class="fa fa-check"></i>
            </span>
            <span class="fin-ss-text">{{ selectAllLabel }}</span>
          </button>
          <button
            v-if="!multiple && !grouped"
            type="button"
            class="fin-ss-option"
            :class="{ checked: !selected.length }"
            @click="chooseSingle('')"
          >
            <span class="fin-ss-check" aria-hidden="true">
              <i v-if="!selected.length" class="fa fa-check"></i>
            </span>
            <span class="fin-ss-text">{{ allLabel }}</span>
          </button>
          <button
            v-for="row in displayRows"
            :key="row.role + ':' + row.value"
            type="button"
            class="fin-ss-option"
            :class="{
              checked: isRowChecked(row),
              'is-group': row.role === 'group',
              'is-child': row.role === 'child',
            }"
            @click="toggleRow(row)"
          >
            <span class="fin-ss-check" :class="{ round: !multiple }" aria-hidden="true">
              <i v-if="isRowChecked(row)" class="fa fa-check"></i>
            </span>
            <span class="fin-ss-text">{{ row.label }}</span>
            <span v-if="row.hint" class="fin-ss-hint">{{ row.hint }}</span>
          </button>
          <div v-if="!displayRows.length" class="fin-ss-empty">{{ emptyLabel }}</div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: "FinSearchSelect",
  props: {
    options: { type: Array, default: () => [] },
    modelValue: { default: () => [] },
    childValue: { type: Array, default: null },
    multiple: { type: Boolean, default: false },
    allLabel: { type: String, default: "Të gjitha" },
    selectAllLabel: { type: String, default: "" },
    emptyLabel: { type: String, default: "Nuk u gjet asnjë rezultat." },
    searchPlaceholder: { type: String, default: "Kërko..." },
  },
  emits: ["update:modelValue", "update:childValue"],
  data() {
    return {
      open: false,
      query: "",
      menuStyle: {
        position: "fixed",
        top: "-9999px",
        left: "0px",
        width: "320px",
        zIndex: 5000,
      },
    };
  },
  computed: {
    grouped() {
      return (this.options || []).some((opt) => (opt.children || []).length);
    },
    selected() {
      return this.toList(this.modelValue);
    },
    selectedChildren() {
      return this.toList(this.childValue);
    },
    hasSelection() {
      return this.selected.length + this.selectedChildren.length > 0;
    },
    displayRows() {
      const q = this.normalize(this.query);
      const rows = [];
      (this.options || []).forEach((opt) => {
        const children = opt.children || [];
        const selfMatch = !q || this.matches(opt);
        const childMatches = q ? children.filter((child) => this.matches(child) || selfMatch) : children;
        if (children.length) {
          if (!q || selfMatch || childMatches.length) {
            rows.push({ ...opt, role: "group" });
            (q ? childMatches : children).forEach((child) => {
              rows.push({ ...child, role: "child", parentValue: opt.value });
            });
          }
        } else if (!q || selfMatch) {
          rows.push({ ...opt, role: "option" });
        }
      });
      return rows;
    },
    optionValues() {
      return (this.options || []).filter((opt) => !(opt.children || []).length).map((opt) => String(opt.value));
    },
    allOptionsSelected() {
      const values = this.optionValues;
      return values.length > 0 && values.every((value) => this.selected.includes(value));
    },
    triggerLabel() {
      const parentCount = this.selected.length;
      const childCount = this.selectedChildren.length;
      if (!parentCount && !childCount) return this.allLabel;
      if (this.selectAllLabel && !this.grouped && this.optionValues.length > 1 && this.allOptionsSelected) {
        return this.selectAllLabel;
      }
      if (parentCount === 1 && !childCount) {
        const opt = this.findOption(this.selected[0]);
        return this.plainLabel(opt) || this.selected[0];
      }
      if (!parentCount && childCount === 1) {
        const opt = this.findOption(this.selectedChildren[0]);
        return this.plainLabel(opt) || this.selectedChildren[0];
      }
      const parts = [];
      if (parentCount) parts.push(parentCount + (parentCount === 1 ? " pikë" : " pika"));
      if (childCount) parts.push(childCount + (childCount === 1 ? " magazinë" : " magazina"));
      if (!this.grouped) return parentCount === 1 ? this.plainLabel(this.findOption(this.selected[0])) || this.selected[0] : parentCount + " të zgjedhura";
      return parts.join(" · ");
    },
    triggerTitle() {
      return this.triggerLabel;
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.$nextTick(() => {
          this.positionMenu();
          this.$refs.searchInput?.focus?.({ preventScroll: true });
        });
        document.addEventListener("mousedown", this.onDocMouseDown, true);
        window.addEventListener("resize", this.positionMenu);
        window.addEventListener("scroll", this.positionMenu, true);
      } else {
        this.detach();
        this.query = "";
      }
    },
  },
  beforeUnmount() {
    this.detach();
  },
  methods: {
    toList(value) {
      if (Array.isArray(value)) return value.map((item) => String(item)).filter(Boolean);
      if (value == null || value === "") return [];
      return [String(value)];
    },
    normalize(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[└]/g, " ")
        .replace(/\u00a0/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },
    matches(opt) {
      return this.normalize(opt.searchText || opt.label || opt.hint || opt.value).includes(this.normalize(this.query));
    },
    plainLabel(opt) {
      if (!opt || !opt.label) return "";
      return String(opt.label).replace(/^[\s\u00a0└]+/, "");
    },
    findOption(value) {
      const key = String(value);
      for (const opt of this.options || []) {
        if (String(opt.value) === key) return opt;
        const child = (opt.children || []).find((item) => String(item.value) === key);
        if (child) return child;
      }
      return null;
    },
    detach() {
      document.removeEventListener("mousedown", this.onDocMouseDown, true);
      window.removeEventListener("resize", this.positionMenu);
      window.removeEventListener("scroll", this.positionMenu, true);
    },
    toggle() {
      this.open = !this.open;
    },
    close() {
      this.open = false;
    },
    onDocMouseDown(event) {
      const root = this.$refs.root;
      const menu = event.target?.closest?.(".fin-ss-menu");
      if (root?.contains(event.target) || menu) return;
      this.close();
    },
    positionMenu() {
      const el = this.$refs.root;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = Math.max(rect.width, 320);
      let left = rect.left;
      if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
      const maxHeight = 360;
      let top = rect.bottom + 4;
      if (top + maxHeight > window.innerHeight && rect.top > maxHeight) {
        top = rect.top - maxHeight - 4;
      }
      this.menuStyle = {
        position: "fixed",
        top: Math.max(8, top) + "px",
        left: left + "px",
        width: width + "px",
        zIndex: 5000,
      };
    },
    usesChildValue() {
      return Array.isArray(this.childValue);
    },
    isChecked(value, list) {
      return list.includes(String(value));
    },
    isRowChecked(row) {
      if (row.role === "group" && row.headerOnly) {
        const children = this.visibleChildValues(row.value);
        return children.length > 0 && children.every((id) => this.selected.includes(id) || this.selectedChildren.includes(id));
      }
      if (row.role === "child" && this.usesChildValue()) {
        return this.isChecked(row.value, this.selectedChildren);
      }
      return this.isChecked(row.value, this.selected);
    },
    visibleChildValues(parentValue) {
      return this.displayRows.filter((row) => row.role === "child" && String(row.parentValue) === String(parentValue)).map((row) => String(row.value));
    },
    toggleRow(row) {
      if (!this.multiple) {
        this.chooseSingle(row.value);
        return;
      }
      if (row.role === "group" && row.headerOnly) {
        this.toggleChildrenOf(row.value);
        return;
      }
      if (row.role === "child" && this.usesChildValue()) {
        this.emitList("update:childValue", this.selectedChildren, row.value);
        return;
      }
      this.emitList("update:modelValue", this.selected, row.value);
    },
    toggleChildrenOf(parentValue) {
      const children = this.visibleChildValues(parentValue);
      if (!children.length) return;
      const target = this.usesChildValue() ? this.selectedChildren : this.selected;
      const event = this.usesChildValue() ? "update:childValue" : "update:modelValue";
      const allOn = children.every((id) => target.includes(id));
      let next = [...target];
      if (allOn) {
        next = next.filter((id) => !children.includes(id));
      } else {
        children.forEach((id) => {
          if (!next.includes(id)) next.push(id);
        });
      }
      this.$emit(event, next);
    },
    emitList(event, current, value) {
      const key = String(value);
      const next = current.includes(key) ? current.filter((id) => id !== key) : [...current, key];
      this.$emit(event, next);
    },
    chooseSingle(value) {
      this.$emit("update:modelValue", value || "");
      this.close();
    },
    toggleSelectAll() {
      if (this.allOptionsSelected) {
        this.$emit("update:modelValue", []);
        return;
      }
      this.$emit("update:modelValue", this.optionValues);
    },
    selectFiltered() {
      if (this.usesChildValue()) {
        const parents = [...this.selected];
        this.displayRows.forEach((row) => {
          if (row.role === "group" && !row.headerOnly && !parents.includes(String(row.value))) {
            parents.push(String(row.value));
          }
        });
        this.$emit("update:modelValue", parents);
        return;
      }
      const next = [...this.selected];
      this.displayRows.forEach((row) => {
        if (row.headerOnly) return;
        if (row.role === "group") return;
        const key = String(row.value);
        if (!next.includes(key)) next.push(key);
      });
      this.$emit("update:modelValue", next);
    },
    clear() {
      this.$emit("update:modelValue", this.multiple ? [] : "");
      if (this.usesChildValue()) this.$emit("update:childValue", []);
    },
  },
};
</script>
